import PropTypes from 'prop-types'
import React, { lazy, Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import { Link } from 'react-router-dom'
import routes from 'constants/router'
import findMatchedRoutes, { getLastMatchedRoute } from 'utils/findMatchedRoutes'
import { ROUTES } from 'constants/routes'
import { useAuth } from 'modules/Auth/hooks/useAuth'
import { useAuthState } from 'modules/Auth/hooks/useAuthState'
import { useEmailConfirmedBroadcast } from 'modules/Auth/hooks/useEmailConfirmedBroadcast'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { Trans } from 'react-i18next'
import { PRELOAD_POOL, startBackgroundPreload } from 'store/domains/utils'
import {
  isGuestAccess,
  isPrivateAccess,
  isUnknownRoute,
  shouldBootstrapSession,
} from 'utils/routeAccess'
import { SET_DATA } from 'modules/Auth/types'

const Panels = lazy(() => import('modules/Panels'))

const AuthProvider = ({ children }) => {
  const dispatch = useDispatch()
  const { tryStartSession, startSessionOrLogout, showNotification } = useAuth()
  const { user, isAuthenticated } = useAuthState()
  const pathGen = useGeneratePathI18n()
  const location = useLocation()

  const [refreshToken, setRefreshToken] = useState(() => localStorage.getItem('refresh_token'))
  const hasShownUnconfirmedEmailNotification = useRef(false)
  const sessionBootstrapTokenRef = useRef(null)

  useEmailConfirmedBroadcast()

  const matchedRoutes = useMemo(
    () => findMatchedRoutes(routes, location.pathname),
    [location.pathname],
  )

  const matchedRouteAccess = getLastMatchedRoute(matchedRoutes)?.access

  const isPrivateRoute = useMemo(
    () => isPrivateAccess(matchedRouteAccess),
    [matchedRouteAccess],
  )

  const isGuestRoute = useMemo(
    () => isGuestAccess(matchedRouteAccess),
    [matchedRouteAccess],
  )

  const onStorageHandler = ({ key, newValue }) =>
    key === 'refresh_token' && setRefreshToken(newValue || '')

  const syncRefreshToken = () => setRefreshToken(localStorage.getItem('refresh_token'))

  useEffect(() => {
    window.addEventListener('storage', onStorageHandler)
    window.addEventListener('auth-storage-changed', syncRefreshToken)

    return () => {
      window.removeEventListener('storage', onStorageHandler)
      window.removeEventListener('auth-storage-changed', syncRefreshToken)
    }
  }, [])

  useLayoutEffect(() => {
    const storedRefreshToken = localStorage.getItem('refresh_token')
    const hasRefreshToken = Boolean(storedRefreshToken)

    if (isAuthenticated) {
      sessionBootstrapTokenRef.current = null
      return
    }

    if (!hasRefreshToken) {
      sessionBootstrapTokenRef.current = null
    }

    if (hasRefreshToken) {
      dispatch({ type: SET_DATA, payload: { loading: true } })

      if (sessionBootstrapTokenRef.current !== storedRefreshToken) {
        sessionBootstrapTokenRef.current = storedRefreshToken
        tryStartSession()
      }

      return
    }

    if (isGuestRoute || isUnknownRoute(matchedRoutes)) {
      dispatch({ type: SET_DATA, payload: { loading: false } })
      return
    }

    if (shouldBootstrapSession(matchedRoutes)) {
      startSessionOrLogout()
      return
    }

    dispatch({ type: SET_DATA, payload: { loading: false } })
  }, [
    refreshToken,
    isAuthenticated,
    isGuestRoute,
    matchedRoutes,
    tryStartSession,
    startSessionOrLogout,
    dispatch,
  ])

  useEffect(() => {
    if (user?.is_email_confirmed === true) {
      hasShownUnconfirmedEmailNotification.current = false
      return
    }

    if (user?.is_email_confirmed !== false || hasShownUnconfirmedEmailNotification.current) {
      return
    }

    hasShownUnconfirmedEmailNotification.current = true
    showNotification({
      type: 'warning',
      message: (
        <Trans i18nKey={'user::settings.profile.email.notification.disapproved'}>
          Подтвердите адрес электронной почты. Статус подтверждения можно смотреть в{' '}
          <Link to={pathGen(ROUTES.USER_SETTINGS)}>личном кабинете</Link>
        </Trans>
      ),
    })
  }, [user?.is_email_confirmed, user?.id, pathGen, showNotification])

  const matchedRoute = getLastMatchedRoute(matchedRoutes)

  useEffect(() => {
    if (!isAuthenticated || !isPrivateRoute) {
      return
    }

    let cancelled = false
    let timeoutId = null
    let cancelPreload = () => {}

    timeoutId = setTimeout(() => {
      if (cancelled) {
        return
      }
      cancelPreload = startBackgroundPreload({
        skipRouteKey: matchedRoute?.key,
        pool: PRELOAD_POOL.PRIVATE,
      })
    }, 500)

    return () => {
      cancelled = true
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      cancelPreload()
    }
  }, [isAuthenticated, isPrivateRoute, matchedRoute?.key])

  useEffect(() => {
    if (isAuthenticated || !isGuestRoute) {
      return
    }

    let cancelled = false
    let timeoutId = null
    let cancelPreload = () => {}

    timeoutId = setTimeout(() => {
      if (cancelled) {
        return
      }
      cancelPreload = startBackgroundPreload({
        skipRouteKey: matchedRoute?.key,
        pool: PRELOAD_POOL.GUEST,
      })
    }, 500)

    return () => {
      cancelled = true
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      cancelPreload()
    }
  }, [isAuthenticated, isGuestRoute, matchedRoute?.key])

  return (
    <>
      {children}
      {isAuthenticated && isPrivateRoute && (
        <Suspense fallback={null}>
          <Panels />
        </Suspense>
      )}
    </>
  )
}

AuthProvider.propTypes = {
  children: PropTypes.node,
}

export default AuthProvider
